import React, { useCallback, useMemo, useRef } from 'react';
import { StyleSheet } from 'react-native';
import Constants from 'expo-constants';
import GoogleMapReact from 'google-map-react';
import useMergedRef from '@react-hook/merged-ref';

import type { GoogleMapRef, MapProps } from '../types';
import { Marker } from '../Marker';
import { DEFAULT_ZOOM_LEVEL, MAX_ZOOM_LEVEL } from '../utils';
import { MAP_STYLES } from './map-styles';
import { useMapAutoBounds, useMapOffset } from '../hooks';

// ─────────────────────────────────────────────────────────────────────────────

export const Map = React.forwardRef((props: MapProps, ref) => {
  const {
    draggable = true,
    onPanDrag,
    onRegionChange,
    onMapPress,
    onPinPress,
    onCalloutPress,
    onZoomChange,
    region,
    pins,
    showControls = true,
    offsetY,
    isOffsetEnabled = false,
    isAutoFitBoundsEnabled = false,
  } = props;

  const mapRef = useRef<GoogleMapRef>(null);
  const multiRef = useMergedRef(ref, mapRef);
  const googleMapRef = mapRef.current?.map_;

  const options = useMemo<GoogleMapReact.MapOptions>(
    () => ({ ...MAP_OPTIONS, disableDefaultUI: !showControls }),
    [showControls],
  );

  // ─── Helpers ─────────────────────────────────────────────────────────

  const onChange = useCallback(
    (event: GoogleMapReact.ChangeEventValue) => {
      const { center, zoom, bounds } = event;

      const topLeft = bounds.nw;
      const bottomRight = bounds.se;

      onRegionChange?.({
        ...center,
        zoom,
        topLeft: {
          longitude: topLeft.lng,
          latitude: topLeft.lat,
        },
        bottomRight: {
          longitude: bottomRight.lng,
          latitude: bottomRight.lat,
        },
      });
    },
    [onRegionChange],
  );

  // ─── Effects ─────────────────────────────────────────────────────────

  useMapAutoBounds({
    isEnabled: isAutoFitBoundsEnabled,
    mapRef: googleMapRef,
    region,
    pins,
    offsetY,
  });
  useMapOffset({
    isEnabled: isOffsetEnabled,
    googleMapRef,
    offsetY,
    pins,
  });

  // ─────────────────────────────────────────────────────────────────────

  return (
    <GoogleMapReact
      draggable={draggable}
      ref={multiRef}
      options={options}
      style={StyleSheet.absoluteFillObject}
      bootstrapURLKeys={GOOGLE_MAPS_BOOTSTRAP_URL_KEYS}
      onClick={onMapPress}
      zoom={getMapZoomLevel(region.zoom)}
      center={region}
      onDrag={onPanDrag}
      onZoomAnimationStart={onZoomChange}
      onChange={onChange}
    >
      {pins.map((pin) => (
        <Marker
          key={pin.id}
          {...pin}
          onPinPress={onPinPress}
          onCalloutPress={onCalloutPress}
        />
      ))}
    </GoogleMapReact>
  );
});

// ─── Constants ───────────────────────────────────────────────────────────────

const MAP_OPTIONS: GoogleMapReact.MapOptions = {
  clickableIcons: false,
  styles: MAP_STYLES,
  maxZoom: MAX_ZOOM_LEVEL,
};

const ENV_VARS =
  Constants.manifest2?.extra?.expoClient?.extra ?? Constants.manifest?.extra;
const GOOGLE_API_KEY =
  ENV_VARS?.GOOGLE_API_KEY ??
  /* cspell:disable */
  'AIzaSyAOwUvcwH8zVTzod168FnuwZemtL6fBjaA';

const GOOGLE_MAPS_BOOTSTRAP_URL_KEYS: GoogleMapReact.BootstrapURLKeys = {
  key: GOOGLE_API_KEY,
  libraries: ['places'],
};

// ─── Utils ───────────────────────────────────────────────────────────────────

function getMapZoomLevel(regionZoomLevel?: number) {
  return Math.min(regionZoomLevel ?? DEFAULT_ZOOM_LEVEL, MAX_ZOOM_LEVEL);
}
